/**
 * Constellation configuration shared by the dashboard views.
 *
 * There are two lists on purpose. The engine computes VADASE (velocity) only for
 * GPS, Galileo and the BeiDou MEO satellites; QZSS and the BeiDou geostationary
 * satellites are processed for VARION (TEC) only — their geometry is unusable for
 * a variometric velocity solution. So they show up in the ionosphere views and
 * never in the velocity ones.
 */

export const IONO_CONSTELLATIONS = ["GPS", "GALILEO", "BEIDOU", "QZSS"] as const;
export const VELOCITY_CONSTELLATIONS = ["GPS", "GALILEO", "BEIDOU"] as const;

export type Constellation = (typeof IONO_CONSTELLATIONS)[number];
export type VelocityConstellation = (typeof VELOCITY_CONSTELLATIONS)[number];

/** MongoDB collection slug: station_<id>_<slug>[_<sat>] */
export const CONS_SLUG: Record<Constellation, string> = {
  GPS: "g",
  GALILEO: "e",
  BEIDOU: "c",
  QZSS: "j",
};

/** RINEX satellite letter */
export const CONS_PREFIX: Record<Constellation, string> = {
  GPS: "G",
  GALILEO: "E",
  BEIDOU: "C",
  QZSS: "J",
};

const range = (from: number, to: number) =>
  Array.from({ length: to - from + 1 }, (_, i) => from + i);

export const SAT_RANGES: Record<Constellation, number[]> = {
  GPS: range(1, 32),
  GALILEO: range(1, 50),
  // C01-C05 and C59-C63 are the geostationary satellites (TEC only);
  // C11-C14, C19-C37 and C41-C46 are the MEO, which also feed the velocity.
  BEIDOU: [...range(1, 5), ...range(11, 14), ...range(19, 37), ...range(41, 46), ...range(59, 63)],
  // J01-J10 (RTCM reports them as PRN 193-202)
  QZSS: range(1, 10),
};

/** Satellite label, e.g. G07, C59, J02 */
export function satelliteName(constellation: string, sat: number): string {
  const prefix = CONS_PREFIX[constellation as Constellation] ?? constellation.charAt(0);
  return prefix + (sat < 10 ? "0" + sat : String(sat));
}
